import { useState } from 'react';
import { GuestTable } from '../components/guests/GuestTable';
import { useStatsQuery } from '../hooks/useGuests';
import { useMode } from '../stores/mode-context';

type TriFilter = '1' | '0' | undefined;

function nextFilter(current: TriFilter): TriFilter {
  if (current === undefined) return '1';
  if (current === '1') return '0';
  return undefined;
}

const FILTER_LABELS: Record<string, { pos: string; neg: string }> = {
  pay_date: { pos: 'Оплатили', neg: 'Не оплатили' },
  registered: { pos: 'Зарегистрированы', neg: 'Не зарегистрированы' },
  dinner: { pos: 'Были', neg: 'Не были' },
};

export function DashboardPage() {
  const { data: stats, isError: statsError } = useStatsQuery();
  const { registrationEnabled } = useMode();
  const [filterPayDate, setFilterPayDate] = useState<TriFilter>(undefined);
  const [filterRegistered, setFilterRegistered] = useState<TriFilter>(undefined);
  const [filterDinner, setFilterDinner] = useState<TriFilter>(undefined);
  const [filteredTotal, setFilteredTotal] = useState(0);

  const total = stats?.total ?? 0;
  const paid = stats?.paid ?? 0;
  const registered = stats?.registered ?? 0;
  const dinner = stats?.dinner ?? 0;
  const unpaid = total - paid;
  const unregistered = total - registered;
  const noDinner = total - dinner;

  return (
    <div>
      <div className="relative">
        <div className="pointer-events-none fixed inset-0 overflow-hidden -z-10" aria-hidden="true">
          <div className="absolute top-[5%] -left-[8%] h-[400px] w-[400px] rounded-full bg-gradient-to-br from-ct-primary/10 to-ct-secondary/10 blur-[100px]" />
          <div className="absolute top-[30%] -right-[5%] h-[300px] w-[300px] rounded-full bg-gradient-to-br from-ct-secondary/8 to-ct-primary/8 blur-[80px]" />
        </div>
      </div>
      <div className="mb-4 inline-flex items-center gap-3 rounded-full bg-indigo-50 border border-indigo-100 px-5 py-2">
        <span className="h-2 w-2 rounded-full bg-ct-primary" />
        <span className="text-xs font-bold uppercase tracking-wider text-ct-primary">Панель управления</span>
        {registrationEnabled && (
          <>
            <span className="h-4 w-px bg-indigo-200" />
            <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
            <span className="text-xs font-bold uppercase tracking-wider text-amber-600">Режим регистрации</span>
          </>
        )}
      </div>

      <h1 className="mb-6 text-4xl font-extrabold tracking-tight text-ct-text">
        Участники{' '}
        <span className="bg-gradient-to-r from-ct-primary to-ct-secondary bg-clip-text text-transparent">
          Конференции
        </span>
      </h1>

      {statsError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
          Не удалось загрузить статистику. Проверьте соединение.
        </div>
      )}

      <div className="mb-6 grid grid-cols-4 gap-4">
        <StatCard
          label="Всего"
          value={`${total} / ${filteredTotal}`}
          gradient="from-ct-primary to-ct-secondary"
        />
        <StatCard
          label="Оплата"
          value={paid}
          negValue={unpaid}
          fil={filterPayDate}
          onClick={() => setFilterPayDate(nextFilter(filterPayDate))}
          labels={FILTER_LABELS.pay_date}
          gradient="from-emerald-500 to-emerald-600"
        />
        <StatCard
          label="Ужин"
          value={dinner}
          negValue={noDinner}
          fil={filterDinner}
          onClick={() => setFilterDinner(nextFilter(filterDinner))}
          labels={FILTER_LABELS.dinner}
          gradient="from-amber-500 to-amber-600"
        />
        <StatCard
          label="Регистрация"
          value={registered}
          negValue={unregistered}
          fil={filterRegistered}
          onClick={() => setFilterRegistered(nextFilter(filterRegistered))}
          labels={FILTER_LABELS.registered}
          gradient="from-violet-500 to-violet-600"
        />
      </div>

      <GuestTable
        filterPayDate={filterPayDate}
        filterRegistered={filterRegistered}
        filterDinner={filterDinner}
        onFilteredTotalChange={setFilteredTotal}
      />
    </div>
  );
}

function StatCard({
  label,
  value,
  negValue,
  gradient,
  fil,
  onClick,
  labels,
}: {
  label: string;
  value: string | number;
  negValue?: number;
  gradient: string;
  fil?: TriFilter;
  onClick?: () => void;
  labels?: { pos: string; neg: string };
}) {
  const isPosActive = fil === '1';
  const isNegActive = fil === '0';

  return (
    <button
      onClick={onClick}
      className={`relative flex flex-col rounded-xl bg-ct-surface border border-ct-border overflow-hidden text-left w-full transition-all duration-200 hover:-translate-y-1 hover:shadow-ct-md active:scale-[0.98] ${
        fil
          ? 'shadow-ct-md ring-2 ring-ct-primary/30'
          : 'shadow-ct-sm'
      }`}
    >
      <div className={`h-1.5 w-full bg-gradient-to-r ${gradient}`} />
      <div className="p-4">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-ct-text-muted">{label}</span>
          {onClick && (
            <span className="text-[10px] font-semibold uppercase tracking-wider text-ct-text-muted/60">
              {fil ? 'сброс' : 'фильтр'}
            </span>
          )}
        </div>
        <div className="mt-1 flex items-baseline gap-3">
          <span className="text-3xl font-extrabold tracking-tight text-ct-text">{value}</span>
          {labels && (
            <span className={`text-xs font-semibold ${isPosActive ? 'text-ct-primary underline underline-offset-4 decoration-2' : 'text-ct-text-muted'}`}>
              {labels.pos}
            </span>
          )}
        </div>
        {negValue !== undefined && labels && (
          <div className="mt-1 flex items-baseline gap-3">
            <span className={`text-xl font-bold tracking-tight ${fil ? '' : 'text-ct-text/70'}`}>{negValue}</span>
            <span className={`text-xs font-semibold ${isNegActive ? 'text-ct-primary underline underline-offset-4 decoration-2' : 'text-ct-text-muted'}`}>
              {labels.neg}
            </span>
          </div>
        )}
      </div>
    </button>
  );
}
